import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Upload, Link2, Type, X, Trash2, GripVertical, Star, StarOff, ImageIcon } from "lucide-react";

const CATEGORIES = ["dream", "uni", "neuroscience", "psychology", "motivation", "art", "other"];

export default function DreamBoard() {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addTab, setAddTab] = useState("upload"); // upload | url | text
  const [filter, setFilter] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(filter ? `/dream-pins?category=${filter}` : "/dream-pins");
        setPins(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [filter]);

  const addPin = (p) => setPins((arr) => [...arr, p]);
  const updatePin = (id, patch) => setPins((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const deletePin = async (p) => {
    try {
      await api.delete(`/dream-pins/${p.id}`);
      setPins((arr) => arr.filter((x) => x.id !== p.id));
      setLightbox(null);
      toast.success("Pin removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleHero = async (p) => {
    try {
      const { data } = await api.patch(`/dream-pins/${p.id}`, { is_dashboard_hero_candidate: !p.is_dashboard_hero_candidate });
      updatePin(p.id, data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Drag & drop reorder
  const onDragStart = (id) => setDragId(id);
  const onDropOn = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = pins.map((p) => p.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...pins];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPins(next);
    setDragId(null);
    try {
      await api.post("/dream-pins/reorder", { items: next.map((p, i) => ({ id: p.id, order: i })) });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="pt-2 mb-6 fade-up flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="eyebrow">the wall of why</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Dream Board</h1>
          <p className="text-muted mt-1">Pin what fuels you. Private, always.</p>
        </div>
        <button onClick={() => setAddOpen(true)} className="btn-primary flex items-center gap-2" data-testid="dreamboard-add-btn">
          <Plus size={14} strokeWidth={1.75} /> Add
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="dreamboard-filters">
        <button onClick={() => setFilter(null)} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: filter === null ? "var(--primary)" : "var(--surface-2)", color: filter === null ? "#fff" : "var(--text)", border: "1px solid var(--border)" }} data-testid="dreamboard-filter-all">
          all
        </button>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setFilter(c)} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: filter === c ? "var(--primary)" : "var(--surface-2)", color: filter === c ? "#fff" : "var(--text)", border: "1px solid var(--border)" }} data-testid={`dreamboard-filter-${c}`}>
            {c}
          </button>
        ))}
      </div>

      {loading ? <div className="text-muted">Loading…</div> :
       pins.length === 0 ? (
        <div className="card-soft p-10 text-center fade-up" data-testid="dreamboard-empty">
          <ImageIcon size={40} strokeWidth={1} style={{ color: "var(--primary)" }} className="mx-auto mb-3" />
          <div className="font-hand text-2xl" style={{ color: "var(--accent)" }}>pin what fuels you</div>
          <p className="text-sm text-muted mt-2">Anything that reminds you why you're doing this — an image, a quote, a link.</p>
          <button onClick={() => setAddOpen(true)} className="btn-primary mt-4 inline-flex items-center gap-2"><Plus size={14} strokeWidth={1.75} /> Add your first</button>
        </div>
      ) : (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-3" data-testid="dreamboard-grid">
          {pins.map((p) => (
            <PinCard
              key={p.id}
              pin={p}
              onOpen={() => setLightbox(p)}
              onDelete={() => deletePin(p)}
              onToggleHero={() => toggleHero(p)}
              onDragStart={onDragStart}
              onDropOn={onDropOn}
            />
          ))}
        </div>
      )}

      {addOpen && <AddSheet tab={addTab} setTab={setAddTab} onClose={() => setAddOpen(false)} onAdded={(p) => { addPin(p); setAddOpen(false); }} />}
      {lightbox && <Lightbox pin={lightbox} onClose={() => setLightbox(null)} onDelete={() => deletePin(lightbox)} onToggleHero={() => toggleHero(lightbox)} />}
    </AppShell>
  );
}

function PinCard({ pin, onOpen, onDelete, onToggleHero, onDragStart, onDropOn }) {
  const isText = pin.pin_type === "text";
  const imgSrc = pin.thumb_url ? `${API_BASE.replace("/api", "")}${pin.thumb_url}` : pin.image_url;
  return (
    <div
      className="mb-3 break-inside-avoid rounded-2xl overflow-hidden card-soft-2 group relative cursor-pointer transition-transform hover:-translate-y-0.5"
      draggable
      onDragStart={() => onDragStart(pin.id)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDropOn(pin.id)}
      onClick={onOpen}
      data-testid={`pin-${pin.id}`}
    >
      {isText ? (
        <div className="p-5" style={{ background: "linear-gradient(160deg, var(--grad-a), var(--grad-b))", minHeight: 160 }}>
          <blockquote className="font-display italic text-lg leading-snug" style={{ color: "var(--text)" }}>
            "{pin.text_content}"
          </blockquote>
          {pin.attribution && <div className="mt-2 font-hand text-base" style={{ color: "var(--accent)" }}>{pin.attribution}</div>}
        </div>
      ) : imgSrc ? (
        <img src={imgSrc} alt={pin.caption || ""} className="w-full block" loading="lazy" />
      ) : (
        <div className="p-5 text-sm text-muted italic" style={{ minHeight: 100 }}>
          <Link2 size={14} strokeWidth={1.5} className="inline mr-1" /> {pin.image_url}
        </div>
      )}
      {pin.caption && (
        <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{pin.caption}</div>
      )}
      {pin.category && (
        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.85)", color: "var(--accent)", backdropFilter: "blur(4px)" }}>
          {pin.category}
        </span>
      )}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHero(); }}
          className="w-7 h-7 rounded-full flex items-center justify-center bg-white/85 backdrop-blur-sm"
          data-testid={`pin-hero-${pin.id}`}
          aria-label="Toggle hero"
        >
          {pin.is_dashboard_hero_candidate ? <Star size={12} strokeWidth={2} fill="var(--primary)" color="var(--primary)" /> : <StarOff size={12} strokeWidth={1.5} />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="w-7 h-7 rounded-full flex items-center justify-center bg-white/85 backdrop-blur-sm"
          data-testid={`pin-delete-${pin.id}`}
          aria-label="Delete"
        >
          <Trash2 size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function AddSheet({ tab, setTab, onClose, onAdded }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(20,15,22,0.35)", backdropFilter: "blur(6px)" }} onClick={onClose} data-testid="dreamboard-add-sheet">
      <div className="card-soft p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <span className="eyebrow">add a pin</span>
          <button onClick={onClose} className="p-1" aria-label="Close"><X size={14} strokeWidth={1.5} /></button>
        </div>
        <div className="flex gap-1 mb-4 p-1 rounded-full" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
          {[["upload", Upload], ["url", Link2], ["text", Type]].map(([id, Ic]) => (
            <button key={id} onClick={() => setTab(id)} className="flex-1 py-1.5 rounded-full text-xs font-medium flex items-center justify-center gap-1" style={{ background: tab === id ? "var(--primary)" : "transparent", color: tab === id ? "#fff" : "var(--text)" }} data-testid={`add-tab-${id}`}>
              <Ic size={12} strokeWidth={1.5} /> {id}
            </button>
          ))}
        </div>
        {tab === "upload" && <UploadForm onAdded={onAdded} />}
        {tab === "url" && <UrlForm onAdded={onAdded} />}
        {tab === "text" && <TextForm onAdded={onAdded} />}
      </div>
    </div>
  );
}

function Fields({ caption, setCaption, category, setCategory }) {
  return (
    <>
      <input className="input-soft" placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200} data-testid="pin-caption-input" />
      <select className="input-soft mt-2" value={category || ""} onChange={(e) => setCategory(e.target.value || null)} data-testid="pin-category-select">
        <option value="">no category</option>
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </>
  );
}

function UploadForm({ onAdded }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (caption) fd.append("caption", caption);
      if (category) fd.append("category", category);
      // Let axios/the browser set `multipart/form-data; boundary=...`
      // automatically. Setting Content-Type manually here strips the
      // boundary and FastAPI returns 400 — this was the user-visible bug.
      const { data } = await api.post("/dream-pins/upload", fd);
      onAdded(data);
      toast.success("Pinned.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="text-sm" data-testid="pin-upload-file" />
      <Fields caption={caption} setCaption={setCaption} category={category} setCategory={setCategory} />
      <button onClick={submit} disabled={!file || busy} className="btn-primary w-full disabled:opacity-60" data-testid="pin-upload-submit">{busy ? "Uploading…" : "Pin"}</button>
    </div>
  );
}

function UrlForm({ onAdded }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dream-pins", { pin_type: "url", image_url: url.trim(), caption, category });
      onAdded(data);
      toast.success(data.has_cached_thumbnail ? "Pinned & cached." : "Pinned (couldn't cache — will load from source).");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <input className="input-soft" placeholder="https://…image url" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="pin-url-input" />
      <Fields caption={caption} setCaption={setCaption} category={category} setCategory={setCategory} />
      <button onClick={submit} disabled={!url.trim() || busy} className="btn-primary w-full disabled:opacity-60" data-testid="pin-url-submit">{busy ? "Fetching…" : "Pin"}</button>
    </div>
  );
}

function TextForm({ onAdded }) {
  const [text, setText] = useState("");
  const [attr, setAttr] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dream-pins", { pin_type: "text", text_content: text.trim(), attribution: attr.trim() || null, caption, category });
      onAdded(data);
      toast.success("Pinned.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <textarea className="input-soft" placeholder="Type a quote, thought, aim…" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={500} data-testid="pin-text-input" autoFocus />
      <input className="input-soft" placeholder="— attribution (optional)" value={attr} onChange={(e) => setAttr(e.target.value)} maxLength={120} data-testid="pin-attr-input" />
      <Fields caption={caption} setCaption={setCaption} category={category} setCategory={setCategory} />
      <button onClick={submit} disabled={!text.trim() || busy} className="btn-primary w-full disabled:opacity-60" data-testid="pin-text-submit">{busy ? "Pinning…" : "Pin"}</button>
    </div>
  );
}

function Lightbox({ pin, onClose, onDelete, onToggleHero }) {
  const imgSrc = pin.thumb_url ? `${API_BASE.replace("/api", "")}${pin.thumb_url}` : pin.image_url;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(20,15,22,0.6)", backdropFilter: "blur(10px)" }} onClick={onClose} data-testid="pin-lightbox">
      <div className="max-w-2xl w-full card-soft p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            {pin.category && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--accent)" }}>{pin.category}</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={onToggleHero} className="btn-ghost text-xs flex items-center gap-1" data-testid="lightbox-hero-btn">
              {pin.is_dashboard_hero_candidate ? <><Star size={12} strokeWidth={2} fill="var(--primary)" color="var(--primary)" /> hero</> : <><StarOff size={12} strokeWidth={1.5} /> hero</>}
            </button>
            <button onClick={onDelete} className="btn-ghost text-xs flex items-center gap-1" data-testid="lightbox-delete-btn"><Trash2 size={12} strokeWidth={1.5} /> Delete</button>
            <button onClick={onClose} className="p-1" aria-label="Close" data-testid="lightbox-close-btn"><X size={14} strokeWidth={1.5} /></button>
          </div>
        </div>
        {pin.pin_type === "text" ? (
          <div className="p-6 rounded-2xl" style={{ background: "linear-gradient(160deg, var(--grad-a), var(--grad-b))" }}>
            <blockquote className="font-display italic text-2xl sm:text-3xl leading-snug" style={{ color: "var(--text)" }}>"{pin.text_content}"</blockquote>
            {pin.attribution && <div className="mt-3 font-hand text-lg" style={{ color: "var(--accent)" }}>{pin.attribution}</div>}
          </div>
        ) : imgSrc ? (
          <img src={imgSrc} alt={pin.caption || ""} className="w-full rounded-2xl" />
        ) : (
          <div className="text-sm text-muted italic">Image unavailable.</div>
        )}
        {pin.caption && <p className="mt-3 text-sm" style={{ color: "var(--text)" }}>{pin.caption}</p>}
      </div>
    </div>
  );
}
